import { type PropsWithChildren, useEffect, useRef } from 'react';

import { ALERT_SNAPSHOT_TICK_MS, extendPlaybackPack } from '#entities';
import {
  allSignalsBumped,
  connectionChanged,
  getLastAdvanceAt,
  IS_BYPASS_MODE,
  reportThrottled,
  signalsBumped,
  useAppDispatch,
} from '#shared';

import { BEARER_SUBPROTOCOL, getAuthToken, toBase64Url } from '#shared/api';
import type { AuthTokenGetter } from '#shared/api';

import { runAlertSnapshot } from './alertSnapshot';
import { applyAlertFrame } from './applyAlertFrame';
import { applyDomainEventFrame } from './applyDomainEventFrame';
import { applyEntityFrame } from './applyEntityFrame';
import { applyPresenceFrame } from './applyPresenceFrame';
import {
  applySettingsFrame,
  applyUserParametersFrame,
} from './applySettingsFrames';
import { runCaseCatchup } from './caseCatchup';
import { createCaseTimelineSignal } from './caseTimelineSignal';
import { runDomainEventCatchup } from './domainEventCatchup';
import { entityChangeEventBus } from './entityChangeEventBus';
import { parseInboundFrame } from './serverMessage';
import { runSettingsRefetch } from './settingsRefetch';
import { useDomainEventHistoryBackfill } from './useDomainEventHistoryBackfill';

/** Minimal socket surface used by the provider — lets tests inject a fake. */
export interface EventSocket {
  onopen: (() => void) | null;
  onclose: (() => void) | null;
  onmessage: ((event: { data: unknown }) => void) | null;
  onerror: (() => void) | null;
  close(): void;
}

/**
 * `http(s)://host` → `ws(s)://host/ws/updates`. Token isn't in the URL (would
 * leak into access/OTel logs) — rides in Sec-WebSocket-Protocol instead.
 */
export const buildUpdatesUrl = (baseHttpUrl: string): string => {
  // /^http/ upgrades both https→wss and http→ws in one shot; keep the ^ anchor.
  const wsBase = baseHttpUrl.replace(/^http/, 'ws').replace(/\/+$/, '');
  return `${wsBase}/ws/updates`;
};

// Re-exported, not redefined: the wire format's one definition is in `shared/api`.
export { BEARER_SUBPROTOCOL };

const RECONNECT_BASE_MS = 1_000;
const RECONNECT_MAX_MS = 30_000;

/**
 * How many consecutive never-opened closes before reporting a likely handshake
 * rejection. Above the couple of failures a BE restart or a brief network blip
 * produces, but low enough to fire well inside a shift.
 */
const HANDSHAKE_FAILURE_REPORT_THRESHOLD = 5;

/**
 * How long an open socket may go without a frame reaching the cursor before it
 * is worth reporting (TMDXNC-14). Deliberately slack: organ-transport traffic is
 * diurnal, so a genuinely silent feed overnight is normal and a tight threshold
 * would cry wolf nightly. It is the WEAK signal — the sharp ones are the
 * catch-up reports in `domainEventCatchup` (the server itself says events exist
 * that we do not have) and, BE-side, producer tick liveness. Reported at
 * `warning` like the rest of this file; the ranking is in the reasoning, not in
 * the Sentry level.
 *
 * SCOPE, so the report is not over-read: this measures frames reaching
 * `advanceSeq`, which the reconnect catch-up drain also feeds. A BE whose live
 * publish is entirely broken but whose catch-up works will therefore look
 * healthy here as long as the socket reconnects within the window; that
 * degradation is caught BE-side by domain_event_publish_failures. The baseline
 * also resets on every reconnect, so a socket flapping faster than the threshold
 * never trips this (that case surfaces as the `nop-events:closed` reports).
 */
const FEED_SILENCE_THRESHOLD_MS = 30 * 60 * 1000;
const SILENCE_CHECK_INTERVAL_MS = 5 * 60 * 1000;

// DEFAULT ON. Kill-switch: REACT_APP_EVENT_DRIVEN=false ⇒ no socket, pure polling.
const DEFAULT_ENABLED = import.meta.env.REACT_APP_EVENT_DRIVEN !== 'false';
const defaultBuildUrl = () =>
  buildUpdatesUrl(import.meta.env.REACT_APP_BE_API_URL ?? '');
/**
 * Adapts a real WebSocket to EventSocket without an unchecked cast — a shape
 * drift becomes a compile error here rather than a silent `as`.
 */
const toEventSocket = (ws: WebSocket): EventSocket => {
  const es: EventSocket = {
    onopen: null,
    onclose: null,
    onmessage: null,
    onerror: null,
    close: () => ws.close(),
  };
  ws.onopen = () => es.onopen?.();
  ws.onclose = () => es.onclose?.();
  ws.onmessage = event => es.onmessage?.({ data: event.data });
  ws.onerror = () => es.onerror?.();
  return es;
};

const defaultCreateSocket = (url: string, protocols: string[]): EventSocket =>
  toEventSocket(new WebSocket(url, protocols));

interface NopEventStreamProviderProps extends PropsWithChildren {
  enabled?: boolean;
  getToken?: AuthTokenGetter;
  buildUrl?: () => string;
  createSocket?: (url: string, protocols: string[]) => EventSocket;
  /**
   * Local bypass (TMDXNC-241): with no Cognito session, `getAuthToken` returns
   * null forever, and requiring a token here stranded every bypass dev on
   * polling — an API that answers and a live feed that never connects. The BE
   * gateway admits a tokenless upgrade under its paired `BYPASS_MODE`
   * (TMDXNC-43), so under bypass a null token connects tokenless instead of
   * endlessly retrying. A real token still rides the subprotocol if present.
   * `IS_BYPASS_MODE` is env-driven and never set in production builds.
   */
  bypassMode?: boolean;
}

/**
 * BE update-gateway client: bumps `liveSync` refetch signals on notices and on
 * (re)connect (cold resync), read via `useAggregateRefetch`. Disabled ⇒ pure polling.
 */
export const NopEventStreamProvider = ({
  children,
  enabled = DEFAULT_ENABLED,
  getToken = getAuthToken,
  buildUrl = defaultBuildUrl,
  createSocket = defaultCreateSocket,
  bypassMode = IS_BYPASS_MODE,
}: NopEventStreamProviderProps) => {
  const dispatch = useAppDispatch();
  useDomainEventHistoryBackfill(getToken);

  // Refs let the effect depend only on `enabled`, so a signal-bump re-render never
  // tears down the socket. Do NOT add these to the effect deps — that reconnects every render.
  const getTokenRef = useRef(getToken);
  getTokenRef.current = getToken;
  const buildUrlRef = useRef(buildUrl);
  buildUrlRef.current = buildUrl;
  const createSocketRef = useRef(createSocket);
  createSocketRef.current = createSocket;
  const dispatchRef = useRef(dispatch);
  dispatchRef.current = dispatch;
  const bypassModeRef = useRef(bypassMode);
  bypassModeRef.current = bypassMode;

  useEffect(() => {
    if (!enabled) {
      return;
    }

    let disposed = false;
    const caseTimelineSignal = createCaseTimelineSignal(dispatchRef.current);
    let socket: EventSocket | undefined;
    let reconnectTimer: ReturnType<typeof setTimeout> | undefined;
    let attempts = 0;
    let hasResynced = false;
    // Set when the previous attempt closed without ever opening, which is what a
    // handshake the gateway rejected for auth looks like — see `socket.onclose`.
    let forceTokenRefresh = false;
    // Whether a forced refresh has already been spent since the last successful
    // open. Once it has, the token is known-fresh, so any further never-opened
    // close is something other than a stale token (a server that is simply down)
    // and must not keep hitting Cognito on every backoff tick.
    let forcedRefreshSinceOpen = false;
    // Consecutive closes that never reached `onopen`. Reset on a successful open.
    let neverOpenedCloses = 0;
    // Epoch ms of the current open connection, or null while disconnected. Also
    // the silence baseline before the first frame arrives: a session that has
    // never received anything still deserves the report, and measuring from
    // `null` would exempt exactly the worst case.
    let openedAt: number | null = null;

    const checkFeedSilence = () => {
      if (openedAt === null) {
        return; // disconnected: the reconnect path owns that story
      }
      const last = getLastAdvanceAt() ?? openedAt;
      if (Date.now() - last < FEED_SILENCE_THRESHOLD_MS) {
        return;
      }
      reportThrottled(
        'nop-events:de-feed-silent',
        'nop-events: socket open but no domain events applied',
        {
          // MUST be >= the check interval, or the throttle is a no-op: nothing
          // resets `lastAdvanceAt` during silence, so once the threshold is
          // crossed the condition stays true and every tick reports. At a 60s
          // throttle against a 5min tick that produced ~114 events per tab per
          // quiet night — the exact alert-fatigue outcome this signal is
          // supposed to avoid, which would then bury the real "feed died at
          // 10am" case. One report per threshold window is the intent.
          throttleMs: FEED_SILENCE_THRESHOLD_MS,
          level: 'warning',
          extra: {
            silentForMs: Date.now() - last,
            sinceFirstFrame: last !== openedAt,
          },
        },
      );
    };
    const silenceTimer = setInterval(
      checkFeedSilence,
      SILENCE_CHECK_INTERVAL_MS,
    );

    // A staleness BOUND, not a poll for freshness: the socket already delivers
    // transitions promptly, but delivery is at-most-once, so a `cleared` lost
    // after commit is never retried and only this re-fetch removes the alert
    // (377:E033). Runs whether or not the socket is currently open — a snapshot
    // is exactly what a client with a dead socket needs most.
    const alertSnapshotTimer = setInterval(() => {
      void runAlertSnapshot(dispatchRef.current);
    }, ALERT_SNAPSHOT_TICK_MS);

    const scheduleReconnect = () => {
      attempts += 1;
      const ceiling = Math.min(
        RECONNECT_BASE_MS * 2 ** attempts,
        RECONNECT_MAX_MS,
      );
      const delay = ceiling * (0.5 + Math.random() / 2); // full-ish jitter
      // scheduleReconnect and connect are mutually recursive; connect only runs
      // async (inside setTimeout), so this forward reference is safe.
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      reconnectTimer = setTimeout(() => void connect(), delay);
    };

    const connect = async () => {
      // Whether THIS attempt ever reached an open socket. Drives the auth-retry
      // decision in `onclose`, so it must be per-attempt, not per-effect.
      let opened = false;
      const force = forceTokenRefresh;
      forceTokenRefresh = false;
      if (force) {
        forcedRefreshSinceOpen = true;
      }
      const token = await getTokenRef.current(
        force ? { forceRefresh: true } : undefined,
      );
      if (disposed) {
        return;
      }
      if (!token && !bypassModeRef.current) {
        // Auth may still be initializing at mount — retry instead of silently
        // stranding the stream on polling forever.
        reportThrottled(
          'nop-events:no-token',
          'nop-events: no auth token yet',
          {
            throttleMs: 60_000,
            level: 'warning',
          },
        );
        scheduleReconnect();
        return;
      }
      // Under bypass a null token connects TOKENLESS (empty protocol list ⇒ no
      // Sec-WebSocket-Protocol header) — the BE gateway's paired BYPASS_MODE
      // admits the upgrade without one. See the bypassMode prop doc.
      socket = createSocketRef.current(
        buildUrlRef.current(),
        token ? [BEARER_SUBPROTOCOL, toBase64Url(token)] : [],
      );
      socket.onopen = () => {
        attempts = 0;
        opened = true;
        neverOpenedCloses = 0;
        forcedRefreshSinceOpen = false;
        openedAt = Date.now();
        dispatchRef.current(connectionChanged(true));
        // Cold resync only on the FIRST connect — reconnects rely on the base poll +
        // heartbeat to avoid a thundering-herd bumpAll after a BE restart (TSMED-1472).
        if (!hasResynced) {
          hasResynced = true;
          dispatchRef.current(allSignalsBumped());
        }
        // Cases are excluded from bumpAll — freshness comes from the entity channel via
        // per-reconnect cursor catch-up. Fire-and-forget: must never throw out of `onopen`.
        void runCaseCatchup(dispatchRef.current, getTokenRef.current);
        // Same rationale for the domain-event feed's own seq cursor.
        void runDomainEventCatchup(dispatchRef.current, getTokenRef.current);
        // A no-op until the pack is ready, so the first connect does not race the initial load.
        void extendPlaybackPack({ getToken: getTokenRef.current });
        // Covers BOTH of the snapshot's connect and reconnect obligations from
        // one call site: `connect()` is re-entered by `scheduleReconnect()`, so
        // a reconnect runs `onopen` again. The alert stream has no cursor to
        // drain, so this is a plain re-fetch rather than a catch-up (377:E032).
        void runAlertSnapshot(dispatchRef.current);
        void runSettingsRefetch(dispatchRef.current);
      };
      socket.onmessage = event => {
        const frame = parseInboundFrame(String(event.data));
        if (!frame) {
          return;
        }
        // Legacy UpdateMessage carries no `kind` — guard with `in` before narrowing,
        // since the discriminant isn't shared across the whole union.
        if ('kind' in frame && frame.kind === 'entity') {
          applyEntityFrame(frame, dispatchRef.current);
          return;
        }
        if ('kind' in frame && frame.kind === 'event') {
          entityChangeEventBus.emit(frame);
          caseTimelineSignal.onCaseTouch(frame);
          return;
        }
        if ('kind' in frame && frame.kind === 'domainEvent') {
          dispatchRef.current(applyDomainEventFrame(frame));
          return;
        }
        if ('kind' in frame && frame.kind === 'presence') {
          applyPresenceFrame(frame, dispatchRef.current);
          return;
        }
        if ('kind' in frame && frame.kind === 'alert') {
          dispatchRef.current(applyAlertFrame(frame));
          return;
        }
        if ('kind' in frame && frame.kind === 'settings') {
          dispatchRef.current(applySettingsFrame(frame));
          return;
        }
        if ('kind' in frame && frame.kind === 'userParameters') {
          dispatchRef.current(applyUserParametersFrame(frame));
          return;
        }
        // Legacy UpdateMessage backstop (reconnect cold-resync + heartbeat path).
        dispatchRef.current(signalsBumped(frame.changed));
      };
      socket.onclose = () => {
        openedAt = null;
        // Auth recovery for the socket path (TMDXNC-52). A handshake the gateway
        // rejected for auth and an unreachable server are INDISTINGUISHABLE here:
        // the browser WebSocket API deliberately hides the HTTP status, and both
        // close without ever opening. So this must not redirect — it would bounce
        // the user to SSO during a plain BE outage. Instead do the one thing that
        // fixes the only auth fault this path can fix on its own: ask the next
        // attempt to force-refresh once, so a token gone stale in Amplify's cache
        // is not retried unchanged forever. A refreshed token the BE still rejects
        // surfaces on the HTTP side, where the status IS visible and
        // `handleSessionExpired` owns the single redirect.
        //
        // Exactly ONCE per stale token: `forcedRefreshSinceOpen` stops a sustained
        // outage — where every attempt closes without opening — from forcing a
        // Cognito refresh on every backoff tick, which would be the same collateral
        // damage on auth that the paragraph above refuses to inflict on the user.
        forceTokenRefresh = !opened && !forcedRefreshSinceOpen;
        if (!opened) {
          neverOpenedCloses += 1;
        }
        dispatchRef.current(connectionChanged(false));
        if (!disposed) {
          reportThrottled('nop-events:closed', 'nop-events: socket closed', {
            throttleMs: 60_000,
            level: 'warning',
          });
          // The signal that the generic report above cannot give: a run of closes
          // that never once opened, while HTTP stays healthy enough that nothing
          // else complains. That is what a WS-only rejection looks like (a gateway
          // that refuses the `cc.bearer` subprotocol, a gateway-only authorizer,
          // an oversized token), and without it live-sync degrades to polling
          // silently and forever for that user.
          if (neverOpenedCloses >= HANDSHAKE_FAILURE_REPORT_THRESHOLD) {
            reportThrottled(
              'nop-events:handshake-rejected',
              'nop-events: socket never opened across repeated attempts',
              {
                throttleMs: 10 * 60_000,
                level: 'warning',
                extra: { consecutiveNeverOpenedCloses: neverOpenedCloses },
              },
            );
          }
          scheduleReconnect();
        }
      };
      socket.onerror = () => {
        reportThrottled('nop-events:error', 'nop-events: socket error', {
          throttleMs: 60_000,
          level: 'error',
        });
        socket?.close();
      };
    };

    void connect();

    return () => {
      disposed = true;
      caseTimelineSignal.dispose();
      clearInterval(silenceTimer);
      clearInterval(alertSnapshotTimer);
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
      }
      socket?.close();
    };
  }, [enabled]);

  return <>{children}</>;
};
